import React from 'react';

import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from '@mui/material';

import { ProductOfflineImpact } from '~/services/OrdersService';

interface Props {
  isOpen: boolean;
  impact: ProductOfflineImpact | null;
  productLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}

function ImpactedOrders({ sample }: { sample: ProductOfflineImpact['awaitingDates']['sample'] }) {
  if (!sample.length) {
    return null;
  }

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap">
      {[...new Map(sample.map((item) => [item.orderId, item])).values()].map((item) => (
        <Link key={item.orderId} href={`/purchases/${item.orderId}`} target="_blank" rel="noreferrer">
          {item.orderId.slice(0, 8)}
        </Link>
      ))}
    </Stack>
  );
}

export default function ProductOfflineImpactDialog({ isOpen, impact, productLabel, onCancel, onConfirm }: Props) {
  const awaitingDates = impact?.awaitingDates.total ?? 0;
  const upcomingTrips = impact?.upcomingTrips.total ?? 0;

  return (
    <Dialog open={isOpen} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Take {productLabel} offline?</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning">
            <AlertTitle>Customers have already bought this</AlertTitle>
            Taking it offline removes it from the offer, so these customers will not be able to choose or change their
            dates online. Please check with the campaign manager or hotel first.
          </Alert>

          {awaitingDates > 0 && (
            <Stack spacing={0.5}>
              <Typography variant="subtitle2">
                {awaitingDates} Buy Now Book Later {awaitingDates === 1 ? 'booking has' : 'bookings have'} not chosen
                dates yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                These customers cannot book their dates at all while this is offline.
              </Typography>
              <ImpactedOrders sample={impact?.awaitingDates.sample ?? []} />
            </Stack>
          )}

          {upcomingTrips > 0 && (
            <Stack spacing={0.5}>
              <Typography variant="subtitle2">
                {upcomingTrips} upcoming {upcomingTrips === 1 ? 'trip' : 'trips'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                These customers cannot change their dates while this is offline.
              </Typography>
              <ImpactedOrders sample={impact?.upcomingTrips.sample ?? []} />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" color="warning" onClick={onConfirm}>
          Take offline anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
}
